import {Component} from 'react';
import Keyword from './Keyword.js';
import './KeywordList.scss';
import languages from '../languages.js';
import LanguageInfo from './LanguageInfo.js';
import IconX from './IconX.js';
import Check from './Check.js';
import TextUtils from '../lib/TextUtils.js';

class KeywordList extends Component {

	keywordCheck = {
		id: 'related-keywords-in-text',
		mounted: true
	};

	constructor(props) {
		super(props);

		this.state = {
			newKeyword: '',
			language: languages.find((lang) => lang.langCode === this.props.language),
			error: '',
		}

		this.removeKeyword = this.removeKeyword.bind(this);
		this.removeKeywords = this.removeKeywords.bind(this);
		this.handleKeyDown = this.handleKeyDown.bind(this);
		this.handleChange = this.handleChange.bind(this);
		this.calculateKeywordCoverage = this.calculateKeywordCoverage.bind(this);
		this.checkOptimalKeywordCoverage = this.checkOptimalKeywordCoverage.bind(this);
		this.handleButtonClick = this.handleButtonClick.bind(this);
	}

	removeKeyword(event) {
		event.preventDefault();
		const button = event.target.closest('.keyword-remove');
		let newKeywords = this.props.relatedKeywords.filter((keyword) => keyword.name !== button.dataset.word);
		this.props.saveRelatedKeywords(newKeywords);
	}

	removeKeywords() {
		if (window.confirm('Are you sure you want to remove all related keywords?')) {
			this.props.saveRelatedKeywords([]);
		}
	}

	componentDidUpdate() {
		if (this.props.language !== this.state.language.langCode) {
			this.setState({
				language: languages.find((lang) => lang.langCode === this.props.language),
			});
		}
	}

	async handleButtonClick(event) {
		event.preventDefault();
		await this.props.getOptimalValues();
	}

	handleKeyDown(event) {
		if (event.keyCode === 13 && this.state.newKeyword) {
			let newKeywords = this.state.newKeyword.split(',');
			newKeywords = newKeywords.map((keyword) => {
				return {
					name: keyword.trim(),
					repeat_min: null,
					repeat_max: null,
				}
			});

			let keywords = this.props.relatedKeywords;
			if (newKeywords.length) {
				newKeywords.forEach((newKeyword) => {
					let ok = true;
					if (keywords.length) {
						keywords.forEach((keyword) => {
							if (keyword.name === newKeyword.name) {
								ok = false;
							}
						});
					}

					if (ok) {
						keywords.push(newKeyword);
					}
				});
			}

			this.props.saveRelatedKeywords(keywords);

			this.setState({
				newKeyword: '',
			})
		}
	}

	handleChange(event) {
		this.setState({
			newKeyword: event.target.value
		})
	}

	checkKeyword(keyword) {
		let found = TextUtils.countOccurrences(keyword.name, this.props.pageContent.content);

		return {
			found: found,
			status: found ? 'ok' : 'nok',
		}
	}

	render() {
		let listItems = [];
		let removeKeywordsButton = null;
		const {pageContent, relatedKeywords} = this.props;

		if (relatedKeywords) {
			listItems = relatedKeywords.map((keyword, index) => {
				if (!keyword) {
					return null;
				}
				const keywordState = this.checkKeyword(keyword);
				return (
					<Keyword
						word={keyword.name}
						key={index}
						minCount={keyword.repeat_min}
						maxCount={keyword.repeat_max}
						removeCallback={this.removeKeyword}
						found={keywordState.found}
						status={keywordState.status}
					/>
				)
			});

			removeKeywordsButton = (<p className="main-button"><button className="button secondary" onClick={this.removeKeywords}>Remove all related keywords</button></p>)
		}

		const keywordLoaderButton = pageContent.keyword ? (
			<button className="button primary" onClick={this.handleButtonClick}>Get related keywords and optimal SEO data</button>
			) : (
			<button className="button primary" disabled onClick={this.handleButtonClick}>Get related keywords and optimal SEO data</button>
			);

		const error = this.state.error ? (<p className="error"><IconX /> {this.state.error}</p>) : null;

		return (
			<div className="keyword-list">
				<h3>Related keywords</h3>
				<LanguageInfo
				language={this.state.language}
				toggleSettingsCallback={this.props.toggleSettingsCallback}
				/>
				{error}
				<div className="keyword-loader" id="keyword-loader">
					<p className="main-button">{keywordLoaderButton}</p>
				</div>
				<p>
					<label htmlFor="new-keyword">Add keywords manually (separated by comma) and press Enter</label>
					<input type="text" id="new-keyword" status="" onKeyDown={this.handleKeyDown} onChange={this.handleChange} value={this.state.newKeyword} placeholder="keyword, another keyword" />
				</p>

				<ul className="list">{listItems}</ul>
				{removeKeywordsButton}
				<Check
					id="keyword-coverage"
					label="Related keyword coverage"
					test={this.calculateKeywordCoverage}
					postData={this.props.pageContent}
					reportCallback={this.props.reportCallback}
				/>
				<Check
					id="optimal-keyword-coverage"
					label="Optimal related keyword coverage"
					test={this.checkOptimalKeywordCoverage}
					postData={this.props.pageContent}
					reportCallback={this.props.reportCallback}
				/>
			</div>
		)
	}

	calculateKeywordCoverage() {
		const {relatedKeywords} = this.props;
		if (!relatedKeywords.length) {
			return {
				status: false,
				value: 0,
			}
		}

		let foundKeywords = 0;
		relatedKeywords.forEach((keyword) => {
			let result = this.checkKeyword(keyword);
			if (result.found) {
				foundKeywords ++;
			}
		});

		let keywordCoverage = foundKeywords / relatedKeywords.length * 100;
		return {
			status: !!keywordCoverage,
			value: `${keywordCoverage.toFixed(2)}%`,
		}
	}

	checkOptimalKeywordCoverage() {
		const {relatedKeywords, optimalData} = this.props;
		if (!relatedKeywords.length || !optimalData || (!optimalData.optimalKeywordCoverage && optimalData.optimalKeywordCoverage !== 0)) {
			return {
				status: false,
			}
		}

		let optimalKeywordCoverage = optimalData.optimalKeywordCoverage;
		let foundKeywords = 0;
		relatedKeywords.forEach((keyword) => {
			let result = this.checkKeyword(keyword);
			if (result.found) {
				foundKeywords ++;
			}
		});

		let keywordCoverage = foundKeywords / relatedKeywords.length * 100;
		keywordCoverage = parseFloat(keywordCoverage.toFixed(2));

		return {
			status: keywordCoverage >= optimalKeywordCoverage,
			optimalMin: optimalKeywordCoverage + '%',
		}

	}
}

export default KeywordList;